import { Container, Graphics, Sprite, Texture } from "pixi.js";

import type { IOverlayElementVideo } from "../../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Overlay.interface";

interface IVideoRenderArgs {
  element: IOverlayElementVideo;
  destination: { width: number; height: number };
}

/**
 * Pixi equivalente del DOM `OverlayElementVideo`. Mismo contrato que
 * `OverlayImageRenderer`: `render()` síncrono que posiciona el wrapper y, cuando
 * el video tiene metadata, agrega el sprite ajustado. La `VideoSource` de Pixi
 * se auto-actualiza por el ticker (no hay que llamar update() por frame).
 *
 * Cache por `src`: `CustomOverlayRenderer.render` reconstruye el inner container
 * cuando cambia la firma del overlay (no por frame), pero igual cacheamos el
 * `<video>` + textura para no recrear/leak en cada edición y para compartir el
 * mismo elemento si dos slots usan el mismo src.
 */
export class OverlayVideoRenderer {
  private cache = new Map<
    string,
    Promise<{ video: HTMLVideoElement; texture: Texture }>
  >();

  render(container: Container, args: IVideoRenderArgs): void {
    const { element, destination } = args;
    const { src, fit, loop, muted } = element.options;

    const wrapper = new Container();

    if (element.rotation) {
      const rad = (element.rotation * Math.PI) / 180;
      const pivotX = destination.width / 2;
      const pivotY = destination.height / 2;
      wrapper.pivot.set(pivotX, pivotY);
      wrapper.position.set(
        element.bounds.left + pivotX,
        element.bounds.top + pivotY,
      );
      wrapper.rotation = rad;
    } else {
      wrapper.position.set(element.bounds.left, element.bounds.top);
    }

    container.addChild(wrapper);

    if (!src) return;

    void this.loadVideo(src, loop !== false, muted !== false)
      .then(({ texture }) => {
        if (wrapper.destroyed) return;
        const node = this.buildFittedNode(
          texture,
          destination.width,
          destination.height,
          fit,
        );
        if (node) wrapper.addChild(node);
      })
      .catch(() => {
        // Slot vacío si falla la carga.
      });
  }

  private loadVideo(
    src: string,
    loop: boolean,
    muted: boolean,
  ): Promise<{ video: HTMLVideoElement; texture: Texture }> {
    const cached = this.cache.get(src);
    if (cached) {
      // La cache es por `src`; un toggle posterior de loop/muted debe reflejarse
      // sin recrear el <video> (antes quedaba stale con el valor de creación).
      void cached.then(({ video }) => {
        video.loop = loop;
        video.muted = muted;
      });
      return cached;
    }

    const pending = new Promise<{
      video: HTMLVideoElement;
      texture: Texture;
    }>((resolve, reject) => {
      const video = document.createElement("video");
      video.crossOrigin = "anonymous";
      video.loop = loop;
      video.muted = muted;
      video.autoplay = true;
      video.playsInline = true;
      video.onloadedmetadata = () => {
        try {
          const texture = Texture.from(video);
          void video.play().catch(() => {});
          resolve({ video, texture });
        } catch (err) {
          reject(err);
        }
      };
      video.onerror = () => reject(new Error("video load failed"));
      video.src = src;
    });

    this.cache.set(src, pending);
    pending.catch(() => this.cache.delete(src));
    return pending;
  }

  private buildFittedNode(
    texture: Texture,
    destW: number,
    destH: number,
    fit: IOverlayElementVideo["options"]["fit"],
  ): Container | null {
    if (destW <= 0 || destH <= 0) return null;

    const sprite = new Sprite(texture);
    const texW = texture.width || destW;
    const texH = texture.height || destH;

    if (fit === "fill") {
      sprite.width = destW;
      sprite.height = destH;
      return sprite;
    }

    const scale =
      fit === "cover"
        ? Math.max(destW / texW, destH / texH)
        : Math.min(destW / texW, destH / texH);
    const renderedW = texW * scale;
    const renderedH = texH * scale;

    sprite.width = renderedW;
    sprite.height = renderedH;
    sprite.x = (destW - renderedW) / 2;
    sprite.y = (destH - renderedH) / 2;

    if (fit === "cover") {
      const out = new Container();
      out.addChild(sprite);
      const mask = new Graphics();
      mask.rect(0, 0, destW, destH).fill(0xffffff);
      out.addChild(mask);
      out.mask = mask;
      return out;
    }

    return sprite;
  }
}

export default OverlayVideoRenderer;
